import {gql, useQuery} from '@apollo/client';
import {GridColDef} from '@mui/x-data-grid-pro';
import {
  ContactATRECQStatusEnum,
  InvitationReviewsInput,
} from '../../schema-types/globalTypes';

import {ReviewInvitationSummaryFragment} from '../../schema-types/ReviewInvitationSummaryFragment';
import {REVIEW_INVITATION_SUMMARY_FRAGMENT} from '../../graphql/fragments';
import {GetInvitations, GetInvitationsVariables} from '../../schema-types/GetInvitations';
import {GetPadStaff} from '../../schema-types/GetPadStaff';
import {GetInvitationStats} from '../../schema-types/GetInvitationStats';
import {toUUIDOrNull} from '../../graphql/uuid';

export const GET_INVITATIONS = gql`
  query GetInvitations(
    $inviteStatus: InvitationReviewsInput!
    $ownerId: UUID
    $search: String
    $ecqStatus: ContactATRECQStatusEnum
    $atrStatus: ContactATRECQStatusEnum
  ) {
    getInvitationReviews(
      inviteStatus: $inviteStatus
      ownerId: $ownerId
      search: $search
      ecqStatus: $ecqStatus
      atrStatus: $atrStatus
    ) {
      reviews {
        ...ReviewInvitationSummaryFragment
      }
      errors
      ok
    }
  }
  ${REVIEW_INVITATION_SUMMARY_FRAGMENT}
`;

interface UseGetInvitationsProps {
  inviteStatus: InvitationReviewsInput;
  staff?: string;
  search?: string;
  ecqStatus?: ContactATRECQStatusEnum;
  atrStatus?: ContactATRECQStatusEnum;
}

export const useGetInvitations = ({
  inviteStatus,
  staff,
  search,
  ecqStatus,
  atrStatus,
}: UseGetInvitationsProps) => {
  const {data, error, loading} = useQuery<GetInvitations, GetInvitationsVariables>(
    GET_INVITATIONS,
    {
      fetchPolicy: 'network-only',
      variables: {
        inviteStatus,
        ownerId: staff === 'ALL' ? null : toUUIDOrNull(staff),
        search,
        ecqStatus: ecqStatus || null,
        atrStatus: atrStatus || null,
      },
    }
  );

  return {data, error, loading};
};

const GET_PAD_STAFF = gql`
  query GetPadStaff {
    allPadAdvisers(advisersOnly: false) {
      id
      firstName
      lastName
    }
  }
`;

export const useGetEmployees = () => {
  const {data, error, loading} = useQuery<GetPadStaff>(GET_PAD_STAFF, {
    fetchPolicy: 'network-only',
  });

  const employees = data?.allPadAdvisers || [];

  return {error, loading, employees};
};

const GET_INVITATION_STATS = gql`
  query GetInvitationStats {
    getInvitationStats {
      id
      numberOfOverdueReviewsNeedingInvitations
      numberOfUpcomingReviewsNeedingInvitations
      numberOfOutstandingInvitations
      numberOfScheduledInvitations
      numberOfCompletedInvitations
      numberOfDeclinedInvitations
      numberOfUncontactableInvitations
      ok
      errors
    }
  }
`;

export const useGetInvitationStats = () => {
  const {data, error, loading} = useQuery<GetInvitationStats>(GET_INVITATION_STATS, {
    fetchPolicy: 'network-only',
  });

  const stats = data?.getInvitationStats || null;

  return {error, loading, stats};
};

export type InvitationColumnProps = GridColDef<
  ReviewInvitationSummaryFragment,
  ReviewInvitationSummaryFragment
> & {
  field: keyof ReviewInvitationSummaryFragment | string;
};
